import { h, Component } from 'preact';
import { connect } from 'react-redux';
import reduce from '../reducers';
import * as actions from '../actions';
import store from '../store';
import util, { getSvgPath } from '../util';
import constants from '../constants';

import Renderer from '../canvas/renderer';

import PositionTools from './PositionTools';
import ResizeWidget from './ResizeWidget';
import GroupBarListItem from "./GroupBarListItem";
import { canDisplayAlign } from "../canvas/component";
import Weight from "./svg/Weight";
import Style from "./svg/Style";
import TextGraphicAnimations from './TextGraphicAnimations';
import { UnlockOutlined, LockOutlined, DeleteOutlined, DownOutlined } from "@ant-design/icons";

export default @connect(reduce, actions)
class ShapeBar extends Component {
  constructor() {
    super();
    this.state = {
      customColor: '',
      isLine: false,
      isSquare: false,
    };

    this.copyElement = this.copyElement.bind(this);
    this.copyGroup = this.copyGroup.bind(this);
    this.removeElement = this.removeElement.bind(this);
    this.removeGroup = this.removeGroup.bind(this);
    this.checkShape = this.checkShape.bind(this);
    this.updateLineStyle = this.updateLineStyle.bind(this);
    this.updateLineHeight = this.updateLineHeight.bind(this);

    this.ungroup = this.ungroup.bind(this);
    this.updateOpacity = this.updateOpacity.bind(this);
    this.updateRadius = this.updateRadius.bind(this);
    this.toggleLock = this.toggleLock.bind(this);

    this.openColorPicker = (type) => {
      util.openColorPicker(type);
    }

    this.getLineCss = () => {
      const $element = this.props.$element;
      if (!$element) return;
      return getSvgPath($element).attr('stroke');
    }

    this.getFillCss = () => {
      const $element = this.props.$element;
      if (!$element) return;
      if ($element.attr('data-color')) {
        return util.getCssFromColorData($element.attr('data-color'));
      } else {
        return getSvgPath($element).attr('fill');
      }
    }

    this.openAnimations = () => {
      util.openAnimations();
    }
  }

  copyElement() {
    const $copy = Renderer.copy(this.props.isMultiSelect ? this.props.$elements : this.props.$element);
    $copy.click();
  }

  copyGroup() {
    Renderer.copy(this.props.$element.parents('.group'));
  }

  removeElement() {
    this.props.deselect();
    Renderer.remove(this.props.isMultiSelect ? this.props.$elements : this.props.$element, this.props.isMultiSelect);
  }

  removeGroup() {
    this.props.deselect();
    Renderer.remove(this.props.$element.parents('.group'));
  }

  checkShape() {
    const $element = this.props.$element;
    if (!$element) return;

    const isLine = util.isLine($element);
    if (this.state.isLine !== isLine) {
      this.setState({ isLine });
    }

    const isSquare = util.isSquare($element);
    if (this.state.isSquare !== isSquare) {
      this.setState({ isSquare });
    }
  }

  updateLineStyle(style) {
    const $element = this.props.$element;

    const $svg = $element.find('svg');
    const $path = $svg.children().first();

    const width = $svg.width();
    const originalWidth = $element.attr('data-original-width');

    if (util.isLine($element)) {
      $svg.attr('viewBox', `0 0 ${originalWidth} 16`);
      $path.attr({
        x2: originalWidth
      });
    }

    $svg.css('height', '100%');
    let parentWidth = $element.parent().width();
    let widthPercent = util.pixelToPercent(width, parentWidth);
    $element.css('width', widthPercent + '%');

    Renderer.updateElementLineStyle($element, style);
  }

  updateLineHeight(height) {
    Renderer.updateElementLineHeight(this.props.$element, height);
    this.props.updateElementAttribute('strokeWidth', parseInt(height));
  }

  updateOpacity(ev) {
    const value = ev.target.value / 100;
    const $element = this.props.$element;

    let colorData = $element.attr('data-color') ? JSON.parse($element.attr('data-color')) :
      { mode: 'solid', colors: [getSvgPath($element).attr('fill'), '#000'] };
    colorData = Renderer.addAlphaToColorData(colorData, value);

    Renderer.updateElementFillColor($element, colorData, value);
    this.props.updateElementAttribute('opacity', value * 100);
  }

  ungroup() {
    Renderer.ungroup(this.props.$element);
  }

  updateRadius(ev) {
    const val = ev.target.value;
    const $elem = util.getElementContent(this.props.$element).find('rect');

    Renderer.updateShapeRadius($elem, 'rx', val);
    this.props.updateElementAttribute('borderRadius', val);
  }

  toggleLock() {
    const $elements = this.props.isMultiSelect ? this.props.$elements : this.props.$element;
    if (this.props.isLocked) {
      Renderer.unlock($elements);
    } else {
      Renderer.lock($elements);
    }
    this.props.updateElementAttribute('isLocked', !this.props.isLocked);
  }

  componentDidMount() {
    this.checkShape();
  }

  componentDidUpdate(props, state, ctx) {
    this.checkShape();
  }

  render(props, state) {
    const lineStyle = [
      { 'strokeWidth': '3', 'strokeLinecap': '', 'strokeDasharray': '' },
      { 'strokeWidth': '3', 'strokeLinecap': 'round', 'strokeDasharray': '0.5,14' },
      { 'strokeWidth': '3', 'strokeLinecap': '', 'strokeDasharray': '5,5' },
      { 'strokeWidth': '3', 'strokeLinecap': '', 'strokeDasharray': '10,10' },
      { 'strokeWidth': '3', 'strokeLinecap': '', 'strokeDasharray': '20,10,5,5,5,10' }
    ];

    const lineStyleContent = lineStyle.map((style) =>
      <li>
        <a onClick={() => this.updateLineStyle(style)} style={{paddingLeft: 10, paddingRight: 10, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
          <svg height="20" width="300">
            <path d="M5 10 l315 0" stroke="black" strokeWidth={style.strokeWidth} strokeLinecap={style.strokeLinecap} strokeDasharray={style.strokeDasharray}>
            </path>
          </svg>
        </a>
      </li>
    );

    const lineHeight = [1, 3, 5, 7, 9, 11, 13];
    const strokeWeightContent = lineHeight.map((height) =>
      <li className={height === props.strokeWidth ? 'active' : ''}>
        <a onClick={() => this.updateLineHeight(height)}>
          {height}
        </a>
      </li>
    );

    const copyOptions = props.isInGroup ?
      <a className={!props.isMobileVersion ? 'icn rwd-icon hint--bottom' : 'mobile-tool-widget'} data-hint="Duplicate" onClick={this.copyGroup}>
        <svg><use xlinkHref={constants.appPrefix + "/svg/icons.svg#app-copy"} /></svg>
        {props.isMobileVersion && <span>Copy</span>}
      </a>
      :
      <a className={!props.isMobileVersion ? 'icn rwd-icon hint--bottom' : 'mobile-tool-widget'} data-hint="Duplicate" onClick={this.copyElement}>
        <svg><use xlinkHref={constants.appPrefix + "/svg/icons.svg#app-copy"} /></svg>
        {props.isMobileVersion && <span>Copy</span>}
      </a>;

    const removeOptions = props.isInGroup ?
      <div className="dropdown sizes">
        <a className={`dropdown-toggle ${!props.isMobileVersion ? 'icn rwd-icon hint--bottom' : 'mobile-tool-widget'}`} data-hint="Delete">
          <DeleteOutlined />
          {props.isMobileVersion && <span>Delete</span>}
        </a>

        <div className="dropdown-menu" style="right: 0; width: auto;">
          <ul className="slider-list">
            <li>
              <a onClick={this.removeElement}>Delete</a>
            </li>
            <li>
              <a onClick={this.removeGroup}>Remove Group</a>
            </li>
          </ul>
        </div>
      </div>
      :
      <a className={!props.isMobileVersion ? 'hint--bottom rwd-icon' : 'mobile-tool-widget'} data-hint="Delete" onClick={this.removeElement}>
        <DeleteOutlined />
        {props.isMobileVersion && <span>Delete</span>}
      </a>;

    const elementOptions = !props.isLocked ?
      <ul className="secondary">
        <li className={!props.isInGroup ? 'hidden' : ''}>
          <a className={!props.isMobileVersion ? 'icn rwd-icon ungroup-btn' : 'mobile-tool-widget'} onClick={this.ungroup}>
            <span>Ungroup</span>
          </a>
        </li>

        {props.multiselect && <GroupBarListItem {...props} />}
        {canDisplayAlign(props.$element) && <li>
          <PositionTools />
        </li>}

        {/*<li>
            <HyperlinkWidget $element={props.$element} href={props.href} isHyperlink={props.isHyperlink} updateElementAttribute={props.updateElementAttribute} hasPdfPermission={props.hasPdfPermission} setActiveMember={props.setActiveMember} />
        </li>*/}

        {!props.isMobileVersion && <span className="separator"></span>}

        {!props.isMobileVersion && <li>
          {copyOptions}
        </li>}
        <li>
          <a className={!props.isMobileVersion ? 'icn rwd-icon hint--bottom' : 'mobile-tool-widget'} data-hint="Lock" onClick={this.toggleLock}>
            <UnlockOutlined />
            {props.isMobileVersion && <span>Lock</span>}
          </a>
        </li>

        {!props.isMobileVersion &&
          <li>
            {removeOptions}
          </li>}
      </ul>
      :
      <ul className="secondary">
        <li>
          <a className={!props.isMobileVersion ? 'icn tgl act rwd-icon hint--bottom' : 'act mobile-tool-widget'} data-hint="Unlock" onClick={this.toggleLock}>
            <LockOutlined />
            {props.isMobileVersion && <span>Unlock</span>}
          </a>
        </li>
        <li>
          <a className="locked-label hint--bottom" data-hint="Unlock" onClick={this.toggleLock}>Locked</a>
        </li>
      </ul>;

    const asideView = store.getState().view;

    return (
      <div className="context-bar">
        <ul>
          {/* {!props.isMobileVersion &&
            <li>
              {removeOptions}
            </li>} */}

          {!props.multiselect && <li>
            {!props.isMobileVersion && <a style="width: 80px; flex-direction: row-reverse" onClick={() => this.openColorPicker(constants.pickerTypes.shapeLine)}>
              <span style="margin-right: auto;" className='lbl'>Line</span>
              <span className="color-box" style={`background: ${this.getLineCss()}; float: none;`}></span>
            </a>}
            {props.isMobileVersion && <a className="mobile-tool-widget" onClick={() => this.openColorPicker(constants.pickerTypes.shapeLine)}>
              <span className="color-box" style={`background: ${this.getLineCss()};`}></span>
              <span>Line</span>
            </a>}
          </li>}

          {!props.multiselect && <li className={state.isLine ? 'hidden' : ''}>
            {!props.isMobileVersion && <a style="width: 80px; flex-direction: row-reverse" onClick={() => this.openColorPicker(constants.pickerTypes.shapeFill)}>
              <span style="margin-right: auto;" className='lbl'>Fill</span>
              <span className="color-box" style={`background: ${this.getFillCss()}; float: none;`}></span>
            </a>}
            {props.isMobileVersion && <a className="mobile-tool-widget" onClick={() => this.openColorPicker(constants.pickerTypes.shapeFill)}>
              <span className="color-box" style={`background: ${this.getFillCss()};`}></span>
              <span>Fill</span>
            </a>}
          </li>}

          {!props.multiselect && <li>
            <div className="dropdown sizes">
              <a className={`dropdown-toggle ${!props.isMobileVersion ? 'car' : 'mobile-tool-widget'}`}>
                {props.isMobileVersion && <Weight />}
                <span>Weight</span>
              </a>
              <div className="dropdown-menu">
                {props.isMobileVersion && <div className="mobile-dropdown-header">
                  <span>Weight</span>
                  <div className="dropdown-toggle"><DownOutlined /></div>
                </div>}
                <ul className="slider-list">
                  {strokeWeightContent}
                </ul>
              </div>
            </div>
          </li>}

          {!props.multiselect && <li>
            <div className="dropdown sizes">
              <a className={`dropdown-toggle ${!props.isMobileVersion ? 'car' : 'mobile-tool-widget'}`}>
                {props.isMobileVersion && <Style />}
                <span>Style</span>
              </a>
              <div className="dropdown-menu">
                {props.isMobileVersion && <div className="mobile-dropdown-header">
                  <span>Style</span>
                  <div className="dropdown-toggle"><DownOutlined /></div>
                </div>}
                <ul className="slider-list">
                  {lineStyleContent}
                </ul>
              </div>
            </div>
          </li>}

          {!props.multiselect && !props.isMobileVersion && <span className="separator"></span>}

          {!props.multiselect && <li>
            <div className="dropdown sizes">
              <a className={`dropdown-toggle ${!props.isMobileVersion ? 'icn rwd-icon hint--bottom' : 'mobile-tool-widget'}`} data-hint="Opacity">
                <svg><use xlinkHref={constants.appPrefix + "/svg/icons.svg#app-opacity"} /></svg>
                {props.isMobileVersion && <span>Opacity</span>}
              </a>
              <div className="dropdown-menu auto" data-close="no">
                {props.isMobileVersion && <div className="mobile-dropdown-header">
                  <span>Opacity</span>
                  <div className="dropdown-toggle"><DownOutlined /></div>
                </div>}
                <ul className="slider-list">
                  <li className="slider-item">
                    <div className={'slider-wrap'}>
                      <label>Opacity</label>
                      <div className="toolbar-wrap">
                        <input className="slider" type="range" min="0" max="100" step="1" value={props.opacity} onChange={this.updateOpacity} />
                      </div>
                      <input className="value" type="number" min="0" max="100" step="1" disabled="disabled" value={Math.round(props.opacity)} />
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </li>}

          {!props.multiselect && state.isSquare && !props.isMobileVersion && <span className="separator"></span>}

          {!props.multiselect && <li className={!state.isSquare ? 'hidden' : ''}>
            <div className="dropdown sizes">
              <a className={`dropdown-toggle ${!props.isMobileVersion ? 'icn rwd-icon hint--bottom' : 'mobile-tool-widget'}`} data-hint="Radius">
                <svg><use xlinkHref={constants.appPrefix + "/svg/icons.svg#app-radius"} /></svg>
                {props.isMobileVersion && <span>Border Radius</span>}
              </a>
              <div className="dropdown-menu auto" data-close="no">
                {props.isMobileVersion && <div className="mobile-dropdown-header">
                  <span>Border Radius</span>
                  <div className="dropdown-toggle"><DownOutlined /></div>
                </div>}
                <ul className="slider-list">
                  <li className="slider-item">
                    <div className={'slider-wrap'}>
                      <label className="font-spacing">Border Radius</label>
                      <div className="toolbar-wrap">
                        <input className="slider" type="range" min="0" max="100" step="1" value={props.borderRadius} onChange={this.updateRadius} onMouseMove={this.updateRadius} />
                      </div>
                      <input className="value" type="number" min="0" max="100" step="1" disabled="disabled"
                        value={props.borderRadius} />
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </li>}

          {!props.multiselect && (props.isAdmin || props.isDesigner) && <span className="separator"></span>}
          {(props.isAdmin || props.isDesigner) && <li className={props.shapeType === 'star' ? 'hidden' : ''}>
            <ResizeWidget hideHeight={props.shapeType === 'line'} />
          </li>}

          {!props.isMobileVersion && <li className={'hideThirdSet'}>
            <a className={`${asideView === 'animations' || props.isAnimated ? "text-effect-active" : ""}`} onClick={this.openAnimations}>
              <span>Animate</span>
            </a>
          </li>}

          {props.isMobileVersion && <li className='hideThirdSet'>
            <div className="dropdown sizes effect-animation-mobile">
              <a className={`dropdown-toggle ${props.isAnimated ? "text-effect-active" : ""} mobile-tool-widget`}>
                <svg><use xlinkHref={constants.appPrefix + "/svg/icons.svg#app-animations"} /></svg>
                <span>Animate</span>
              </a>

              <div className="dropdown-menu right" data-close="no">
                {props.isMobileVersion && <div className="mobile-dropdown-header">
                  <span>Animate</span>
                  <div className="dropdown-toggle"><DownOutlined /></div>
                </div>}
                <div className="option-container" style="height: 50vh; overflow: auto;">
                  <TextGraphicAnimations/>
                </div>
              </div>
            </div>
          </li>}
        </ul>
        {elementOptions}
      </div>
    )
  }
}
